import {
  Block,
  Close,
  Download,
  FilterAltOutlined,
  IosShare,
  KeyboardArrowLeft,
  KeyboardArrowRight,
  PeopleAlt,
  UploadFile,
  WarningAmber,
} from "@mui/icons-material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  MenuItem,
  OutlinedInput,
  Select,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from "@mui/material";
import { saveAs } from "file-saver";
import React, { useContext, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router";
import { toast } from "react-toastify";
import { CustomButton } from "src/Components/Common/Button/CustomButton";
import { LoggedInSkeleton } from "src/Components/Common/LoggedInSkeleton";
import { getReconTopBarButtons } from "src/Components/Common/TopNavBar";
import useFetch from "src/Components/Common/useFetch";
import { userContext } from "src/Components/Contexts/userContext";
import CustomToast from "src/Components/CustomToast";
import { InstamartInternalProcessRole } from "src/Components/Recon360/DisputeResolution/RejectedInvoices/RejectedInvoices";
import { UserRole } from "src/entity/models/FrontendActor";
import { API_ENDPOINTS } from "src/Utils/ApiConstants/ApiUrlConstants";
import { convertUtcToIst } from "src/Utils/DateUtils";
import { formatMoney, ToDineroObj } from "src/Utils/MoneyUtils";

// ─── Types ────────────────────────────────────────────────────────────────────

interface HoldSummary {
  vendorsOnHold: number;
  fullPaymentBlocked: number;
  totalHoldAmount: number;
  lastUploadDate: string | null;
}

interface HoldBucketDef {
  key: string;
  label: string;
  color: string;
  bgColor: string;
  borderColor?: string;
}

interface HoldTabCount {
  panLevelHold: number;
  fullPaymentHold: number;
  gstRelatedHold: number;
  prRtvRelatedHold: number;
  eventsHold: number;
  adsVisibilityHold: number;
  bdpoHold: number;
}

// ─── Constants ────────────────────────────────────────────────────────────────

export enum OnHoldReportType {
  ENTITY_WISE_HOLD = "entity_wise_hold",
  OTHER_HOLD = "other_hold",
  FULL_PAYMENT_HOLD = "full_payment_hold",
  GST_SCOOTSY = "gst_scootsy",
  PR_RTV_WASTAGE = "pr_rtv_wastage",
  RTV_YET_TO_PICK = "rtv_yet_to_pick",
  PRRTV_UNACCOUNTED = "prrtv_unaccounted",
  EVENTS_HOLD = "events_hold",
  BDPO = "bdpo",
  ADS_VISIBILITY = "ads_visibility",
}

export const HOLD_BUCKETS: HoldBucketDef[] = [
  { key: "GST", label: "GST", color: "#1d4ed8", bgColor: "#dbeafe" },
  { key: "PR/RTV", label: "PR/RTV", color: "#9a3412", bgColor: "#fff7ed", borderColor: "#fdba74" },
  { key: "Events", label: "Events", color: "#374151", bgColor: "#f3f4f6" },
  { key: "Ads Visibility", label: "Ads Visibility", color: "#0f766e", bgColor: "#ccfbf1" },
  { key: "BDPO", label: "BDPO", color: "#065f46", bgColor: "#d1fae5" },
];

const ROWS_PER_PAGE = 25;

const thSx = {
  padding: "8px 12px",
  fontSize: 11,
  fontWeight: 600,
  color: "#6b7280",
  backgroundColor: "#f9fafb",
  borderBottom: "1px solid #e5e7eb",
  whiteSpace: "nowrap",
  userSelect: "none",
  cursor: "pointer",
  "&:hover": { color: "#374151" },
} as const;

const tdSx = {
  padding: "9px 12px",
  fontSize: 12,
  color: "#374151",
  borderBottom: "1px solid #f3f4f6",
} as const;

const TAB_FILTER_MAP: Record<number, string | null> = {
  0: null,
  1: "fullPayment",
  2: "GST",
  3: "PR/RTV",
  4: "Events",
  5: "Ads Visibility",
  6: "BDPO",
};

/** Maps UI tab index → API :tab param. null = no API tab yet. */
const UI_TAB_TO_API_TAB: Record<number, string | null> = {
  0: "entity_hold",
  1: "full_payment_hold",
  2: "gst",
  3: "rtv",
  4: "events_hold",
  5: "ads_visibility",
  6: "bdpo",
};

type ColType = "text" | "currency" | "date" | "boolean";
interface ColDef {
  key: string;
  label: string;
  type: ColType;
}

/** Static column definitions per tab — order is display order. */
const TAB_COLUMN_DEFS: Record<number, ColDef[]> = {
  // Tab 0 — entity_hold
  0: [
    { key: "Hold Buckets", label: "Hold Buckets", type: "text" },
    { key: "Entity", label: "Entity", type: "text" },
    { key: "PAN", label: "PAN", type: "text" },
    { key: "Vendor Name", label: "Vendor Name", type: "text" },
    { key: "Type of Hold", label: "Type of Hold", type: "text" },
    { key: "Total Hold Amount", label: "Total Hold Amount", type: "currency" },
    { key: "Hold Remarks/Reason", label: "Hold Remarks/Reason", type: "text" },
    { key: "Status", label: "Status", type: "text" },
    { key: "Last Updated", label: "Last Updated", type: "date" },
  ],
  // Tab 1 — full_payment_hold
  1: [
    { key: "Hold Buckets", label: "Hold Buckets", type: "text" },
    { key: "Entity Scope", label: "Entity Scope", type: "text" },
    { key: "PAN", label: "PAN", type: "text" },
    { key: "Vendor Name", label: "Vendor Name", type: "text" },
    { key: "Reason", label: "Reason", type: "text" },
    { key: "Status", label: "Status", type: "text" },
    { key: "Requested By", label: "Requested By", type: "text" },
    { key: "Hold Date", label: "Hold Date", type: "date" },
    { key: "Last Updated", label: "Last Updated", type: "date" },
  ],
  // Tab 2 — gst
  2: [
    { key: "Hold Bucket", label: "Hold Bucket", type: "text" },
    { key: "Resolved Status", label: "Resolved Status", type: "text" },
    { key: "PAN", label: "PAN", type: "text" },
    { key: "Vendor Name", label: "Vendor Name", type: "text" },
    { key: "Source Type", label: "Source Type", type: "text" },
    { key: "Requested By", label: "Requested By", type: "text" },
    { key: "FY", label: "FY", type: "text" },
    { key: "Document No", label: "Document No", type: "text" },
    { key: "Document Date", label: "Document Date", type: "date" },
    { key: "Document Value", label: "Document Value", type: "currency" },
    { key: "Total Tax", label: "Total Tax", type: "currency" },
    { key: "Hold Amount", label: "Hold Amount", type: "currency" },
    { key: "Document Type", label: "Document Type", type: "text" },
    { key: "Entity", label: "Entity", type: "text" },
    { key: "Entity GSTIN", label: "Entity GSTIN", type: "text" },
    { key: "Vendor GSTIN", label: "Vendor GSTIN", type: "text" },
    { key: "Status", label: "Status", type: "text" },
    { key: "Last Updated", label: "Last Updated", type: "date" },
  ],
  // Tab 3 — rtv
  3: [
    { key: "Hold Bucket", label: "Hold Bucket", type: "text" },
    { key: "Status", label: "Status", type: "text" },
    { key: "PAN", label: "PAN", type: "text" },
    { key: "Vendor Name", label: "Vendor Name", type: "text" },
    { key: "Entity", label: "Entity", type: "text" },
    { key: "RTV Date", label: "RTV Date", type: "date" },
    { key: "RTV No", label: "RTV No", type: "text" },
    { key: "RTV Amount", label: "RTV Amount", type: "currency" },
    { key: "PO No", label: "PO No", type: "text" },
    { key: "GRN No", label: "GRN No", type: "text" },
    { key: "SKU Name", label: "SKU Name", type: "text" },
    { key: "WH", label: "WH", type: "text" },
    { key: "Hold Reason", label: "Hold Reason", type: "text" },
    { key: "Last Updated", label: "Last Updated", type: "date" },
  ],
  // Tab 4 — events_hold
  4: [
    { key: "Hold Bucket", label: "Hold Bucket", type: "text" },
    { key: "Type of Hold", label: "Type of Hold", type: "text" },
    { key: "Entity", label: "Entity", type: "text" },
    { key: "Vendor Name", label: "Vendor Name", type: "text" },
    { key: "Brand", label: "Brand", type: "text" },
    { key: "PAN", label: "PAN", type: "text" },
    { key: "Hold Reason", label: "Hold Reason", type: "text" },
    { key: "Hold Amount", label: "Hold Amount", type: "currency" },
    { key: "Resolved Status", label: "Resolved Status", type: "boolean" },
    { key: "Status", label: "Status", type: "text" },
    { key: "Last Updated", label: "Last Updated", type: "date" },
  ],
  // Tab 5 — ads_visibility
  5: [
    { key: "PAN", label: "PAN", type: "text" },
    { key: "Vendor Name", label: "Vendor Name", type: "text" },
    { key: "Entity", label: "Entity", type: "text" },
    { key: "Customer ID", label: "Customer ID", type: "text" },
    { key: "Hold Date", label: "Hold Date", type: "date" },
    { key: "Hold Amount", label: "Hold Amount", type: "currency" },
    { key: "Hold Reason", label: "Hold Reason", type: "text" },
    { key: "AR Spoc", label: "AR Spoc", type: "text" },
    { key: "Status", label: "Status", type: "text" },
    { key: "Last Updated", label: "Last Updated", type: "date" },
  ],
  // Tab 6 — bdpo
  6: [
    { key: "PAN", label: "PAN", type: "text" },
    { key: "Vendor Name", label: "Vendor Name", type: "text" },
    { key: "Entity", label: "Entity", type: "text" },
    { key: "Hold Date", label: "Hold Date", type: "date" },
    { key: "Hold Amount", label: "Hold Amount", type: "currency" },
    { key: "Brand", label: "Brand", type: "text" },
    { key: "Hold Reason", label: "Hold Reason", type: "text" },
    { key: "AR Team Member", label: "AR Team Member", type: "text" },
    { key: "Status", label: "Status", type: "text" },
    { key: "Last Updated", label: "Last Updated", type: "date" },
  ],
};

const TAB_NAMES = [
  "PAN Level Hold",
  "Full Payment Hold",
  "GST Related Hold",
  "PR/RTV Related Hold",
  "Events Hold",
  "Ads Visibility Hold",
  "BDPO Hold",
];

/** Row key used to sum the Total Hold Amount per tab. null = tab has no hold amount column. */
const TAB_HOLD_AMOUNT_KEY: Record<number, string | null> = {
  0: "Total Hold Amount",
  1: null,
  2: "Hold Amount",
  3: "RTV Amount",
  4: "Hold Amount",
  5: "Hold Amount",
  6: "Hold Amount",
};

/** Filter keys that carry over when switching tabs — applied only if the target tab has a matching column. */
const CROSS_TAB_FILTER_KEYS = new Set(["Entity", "PAN", "Vendor Name"]);

/** Columns that represent the same filter concept as another column under a different name. */
const FILTER_KEY_ALIASES: Record<string, string> = { "Entity Scope": "Entity" };
const resolveFilterKey = (key: string): string => FILTER_KEY_ALIASES[key] ?? key;

/** Columns rendered as multi-select dropdowns (values populated from data). */
const DROPDOWN_TEXT_COLS = new Set([
  "Hold Buckets",
  "Hold Bucket",
  "Type of Hold",
  "Status",
  "Resolved Status",
  "Source Type",
  "Document Type",
  "FY",
  "Entity Scope",
  "WH",
  "Brand",
  "Entity",
]);

// ─── Sub-components ───────────────────────────────────────────────────────────

/** label → big value → sub-label; icon in rounded-square — matches prototype */
const SummaryCard = ({
  icon,
  iconBg,
  iconColor,
  value,
  label,
  subLabel,
}: {
  icon: React.ReactNode;
  iconBg: string;
  iconColor: string;
  value: React.ReactNode;
  label: string;
  subLabel?: string;
}) => (
  <Card sx={{ flex: 1, border: "1px solid #e5e7eb", borderRadius: 2, boxShadow: "none" }}>
    <CardContent sx={{ display: "flex", alignItems: "center", gap: 1.5, p: "14px 16px !important" }}>
      <Box
        sx={{
          width: 38,
          height: 38,
          borderRadius: "10px",
          backgroundColor: iconBg,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          color: iconColor,
          "& svg": { fontSize: 20 },
        }}
      >
        {icon}
      </Box>
      <Box>
        <Typography sx={{ fontSize: 11, color: "#6b7280", mb: 0.25 }}>{label}</Typography>
        <Typography sx={{ fontSize: 17, fontWeight: 700, color: "#111827", lineHeight: 1.2 }}>{value}</Typography>
        {subLabel && <Typography sx={{ fontSize: 10, color: "#9ca3af", mt: 0.25 }}>{subLabel}</Typography>}
      </Box>
    </CardContent>
  </Card>
);

/**
 * Upload card — matches prototype:
 *   [doc-icon] Title                    Last: timestamp
 *   ┌─── dashed upload zone ──────────────────────────┐
 *   │ [↑]  File title   Subtitle          [Upload]     │
 *   └─────────────────────────────────────────────────┘
 */
const UploadCard = ({
  title,
  fileTitle,
  fileSubtitle,
  lastUpload,
  onUpload,
  uploading,
  reportType,
  role,
}: {
  title: string;
  fileTitle: string;
  fileSubtitle: string;
  lastUpload?: string | null;
  onUpload: (file: File) => void;
  uploading: boolean;
  reportType?: OnHoldReportType;
  role?: InstamartInternalProcessRole;
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [downloading, setDownloading] = useState(false);

  const handleTemplateDownload = () => {
    if (!reportType || !role) return;
    setDownloading(true);
    useFetch(API_ENDPOINTS.DOWNLOAD_VENDOR_HOLD_TEMPLATE(role, reportType).url, "GET", {
      failureMessage: API_ENDPOINTS.DOWNLOAD_VENDOR_HOLD_TEMPLATE(role, reportType).failureMessage,
      thenCallBack: (response) => {
        const binaryStr = atob(response.data.data as string);
        const bytes = new Uint8Array(binaryStr.length);
        for (let i = 0; i < binaryStr.length; i++) bytes[i] = binaryStr.charCodeAt(i);
        saveAs(
          new Blob([bytes], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
          `${title}_Template.xlsx`
        );
        setDownloading(false);
      },
      catchCallBack: () => setDownloading(false),
    });
  };

  return (
    <Card sx={{ flex: 1, border: "1px solid #e5e7eb", borderRadius: 2, boxShadow: "none" }}>
      <CardContent sx={{ p: "13px 15px !important" }}>
        {/* Header row */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.25 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <ArticleOutlinedIcon sx={{ fontSize: 14, color: "#6b7280" }} />
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>{title}</Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {lastUpload && (
              <Typography sx={{ fontSize: 10, color: "#9ca3af" }}>
                Last: {convertUtcToIst(new Date(lastUpload), "DD MMM YYYY, hh:mm A")}
              </Typography>
            )}
            {reportType && (
              <Button
                size="small"
                variant="text"
                disabled={downloading}
                startIcon={downloading ? <CircularProgress size={11} /> : <Download sx={{ fontSize: 13 }} />}
                onClick={handleTemplateDownload}
                sx={{
                  fontSize: 11,
                  color: "#5b2b8e",
                  textTransform: "none",
                  px: 0.75,
                  py: 0.25,
                  minWidth: 0,
                  "&:hover": { backgroundColor: "#f5f0fc" },
                }}
              >
                Template
              </Button>
            )}
          </Box>
        </Box>

        {/* Dashed upload zone */}
        <Box
          onClick={() => !uploading && inputRef.current?.click()}
          sx={{
            border: "1.5px dashed #d1d5db",
            borderRadius: "8px",
            p: "10px 12px",
            display: "flex",
            alignItems: "center",
            gap: 1.25,
            backgroundColor: "#fafafa",
            cursor: uploading ? "not-allowed" : "pointer",
            transition: "border-color .15s, background .15s",
            "&:hover": uploading ? {} : { borderColor: "#7c3aed", backgroundColor: "#f5f0fc" },
          }}
        >
          <Box
            sx={{
              width: 28,
              height: 28,
              border: "1px solid #e5e7eb",
              borderRadius: "6px",
              backgroundColor: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <UploadFile sx={{ fontSize: 14, color: "#6b7280" }} />
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: 11.5, fontWeight: 500, color: "#374151" }}>{fileTitle}</Typography>
            <Typography
              sx={{
                fontSize: 10,
                color: "#9ca3af",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {fileSubtitle}
            </Typography>
          </Box>

          <Button
            variant="outlined"
            size="small"
            disabled={uploading}
            startIcon={uploading ? <CircularProgress size={12} /> : null}
            onClick={(e) => {
              e.stopPropagation();
              inputRef.current?.click();
            }}
            sx={{
              fontSize: 11,
              borderColor: "#5b2b8e",
              color: "#5b2b8e",
              borderRadius: "6px",
              px: 1.5,
              py: 0.5,
              minWidth: 70,
              flexShrink: 0,
              "&:hover": { backgroundColor: "#f5f0fc", borderColor: "#5b2b8e" },
            }}
          >
            Upload
          </Button>
        </Box>

        <input
          ref={inputRef}
          type="file"
          hidden
          accept=".xlsx"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) onUpload(file);
            e.target.value = "";
          }}
        />
      </CardContent>
    </Card>
  );
};

/** Tab label with pill badge — red for PAN/FullPayment, gray for others */
const TabLabel = ({ label, count, danger }: { label: string; count?: number; danger?: boolean }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
    {label}
    {count !== undefined && (
      <Box
        component="span"
        sx={{
          fontSize: "0.65rem",
          fontWeight: 600,
          px: 0.75,
          py: 0.2,
          borderRadius: "100px",
          backgroundColor: danger ? "#fee2e2" : "#f3f4f6",
          color: danger ? "#dc2626" : "#6b7280",
          lineHeight: 1.5,
        }}
      >
        {count.toLocaleString("en-IN")}
      </Box>
    )}
  </Box>
);

// ─── Pagination ──────────────────────────────────────────────────────────────

const PaginationBar = ({
  total,
  page,
  rowsPerPage,
  onPageChange,
  label = "vendors",
}: {
  total: number;
  page: number;
  rowsPerPage: number;
  onPageChange: (p: number) => void;
  label?: string;
}) => {
  const totalPages = Math.max(1, Math.ceil(total / rowsPerPage));
  const from = total === 0 ? 0 : page * rowsPerPage + 1;
  const to = Math.min((page + 1) * rowsPerPage, total);
  const btnSx = (active = false) => ({
    width: 26,
    height: 26,
    minWidth: 0,
    borderRadius: "6px",
    border: "1px solid",
    borderColor: active ? "#5b2b8e" : "#e5e7eb",
    backgroundColor: active ? "#5b2b8e" : "#fff",
    color: active ? "#fff" : "#374151",
    fontSize: 11,
    fontWeight: active ? 600 : 400,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    transition: "background .15s, border-color .15s",
    "&:hover": active ? {} : { backgroundColor: "#f5f0fc", borderColor: "#9b72cf", color: "#5b2b8e" },
  });
  const pages: (number | "...")[] = [];
  if (totalPages <= 7) {
    for (let i = 0; i < totalPages; i++) pages.push(i);
  } else {
    pages.push(0);
    if (page > 2) pages.push("...");
    for (let i = Math.max(1, page - 1); i <= Math.min(totalPages - 2, page + 1); i++) pages.push(i);
    if (page < totalPages - 3) pages.push("...");
    pages.push(totalPages - 1);
  }
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        px: "15px",
        py: "9px",
        borderTop: "1px solid #f3f4f6",
      }}
    >
      <Typography sx={{ fontSize: 12, color: "#6b7280" }}>
        Showing {from}&ndash;{to} of {total.toLocaleString("en-IN")} {label}
      </Typography>
      <Box sx={{ display: "flex", gap: "3px" }}>
        <Box sx={btnSx()} onClick={() => page > 0 && onPageChange(page - 1)}>
          <KeyboardArrowLeft sx={{ fontSize: 14 }} />
        </Box>
        {pages.map((p, i) =>
          p === "..." ? (
            <Box key={`e${i}`} sx={{ ...btnSx(), cursor: "default", "&:hover": {} }}>
              &hellip;
            </Box>
          ) : (
            <Box key={p} sx={btnSx(p === page)} onClick={() => onPageChange(p as number)}>
              {(p as number) + 1}
            </Box>
          )
        )}
        <Box sx={btnSx()} onClick={() => page < totalPages - 1 && onPageChange(page + 1)}>
          <KeyboardArrowRight sx={{ fontSize: 14 }} />
        </Box>
      </Box>
    </Box>
  );
};

// ─── Filter Modal ────────────────────────────────────────────────────────────

const FilterDrawer = ({
  open,
  onClose,
  colDefs,
  rows,
  filters,
  onChange,
  onClear,
}: {
  open: boolean;
  onClose: () => void;
  colDefs: ColDef[];
  rows: Record<string, any>[];
  filters: Record<string, any>;
  onChange: (key: string, value: any) => void;
  onClear: () => void;
}) => {
  const uniqueOpts: Record<string, string[]> = {};
  for (const col of colDefs) {
    if (col.type === "boolean") {
      uniqueOpts[col.key] = ["Resolved", "Pending"];
    } else if (col.type === "text" && DROPDOWN_TEXT_COLS.has(col.key)) {
      const vals = new Set<string>();
      for (const row of rows) {
        if (row[col.key] !== null && row[col.key] !== undefined && String(row[col.key]).trim() !== "") {
          vals.add(String(row[col.key]));
        }
      }
      uniqueOpts[col.key] = Array.from(vals).sort();
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: "12px", overflow: "hidden" } }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2.5,
          py: 1.75,
          borderBottom: "1px solid #e5e7eb",
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <FilterAltOutlined sx={{ fontSize: 16, color: "#5b2b8e" }} />
          <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#111827" }}>Filters</Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Button
            size="small"
            onClick={onClear}
            sx={{ fontSize: 11, color: "#6b7280", textTransform: "none", px: 1, minWidth: 0 }}
          >
            Clear All
          </Button>
          <Box
            onClick={onClose}
            sx={{
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#6b7280",
              p: 0.5,
              borderRadius: "4px",
              "&:hover": { backgroundColor: "#f3f4f6" },
            }}
          >
            <Close sx={{ fontSize: 16 }} />
          </Box>
        </Box>
      </Box>

      {/* Filter fields — 3-column grid */}
      <Box
        sx={{
          overflowY: "auto",
          maxHeight: "65vh",
          px: 2.5,
          py: 2,
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: 2,
        }}
      >
        {colDefs.map((col) => {
          const labelEl = (
            <Typography sx={{ fontSize: 11, fontWeight: 500, color: "#6b7280", mb: 0.5 }}>{col.label}</Typography>
          );
          const inputSx = {
            width: "100%",
            height: 34,
            border: "1px solid #e5e7eb",
            borderRadius: "6px",
            px: "10px",
            fontSize: 12,
            fontFamily: "inherit",
            color: "#374151",
            outline: "none",
            backgroundColor: "#fff",
            boxSizing: "border-box",
            "&:focus": { borderColor: "#5b2b8e" },
          } as const;

          if (col.type === "boolean" || (col.type === "text" && DROPDOWN_TEXT_COLS.has(col.key))) {
            const fKey = resolveFilterKey(col.key);
            const selected = (filters[fKey] as string[]) || [];
            const opts = uniqueOpts[col.key] || [];
            if (opts.length === 0 && selected.length === 0) return null;
            return (
              <Box key={col.key}>
                {labelEl}
                <Select
                  multiple
                  displayEmpty
                  size="small"
                  value={selected}
                  onChange={(e) => onChange(fKey, e.target.value as string[])}
                  input={
                    <OutlinedInput
                      notched={false}
                      sx={{
                        width: "100%",
                        fontSize: 12,
                        height: 34,
                        "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e5e7eb" },
                        "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#e5e7eb" },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#5b2b8e",
                          borderWidth: "1px",
                        },
                      }}
                    />
                  }
                  renderValue={(s) =>
                    (s as string[]).length === 0 ? (
                      <Typography sx={{ fontSize: 12, color: "#9ca3af" }}>Select…</Typography>
                    ) : (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.25 }}>
                        {(s as string[]).map((v) => (
                          <Chip key={v} label={v} size="small" sx={{ fontSize: 10, height: 18 }} />
                        ))}
                      </Box>
                    )
                  }
                  MenuProps={{ PaperProps: { sx: { maxHeight: 240 } } }}
                  sx={{ width: "100%", fontSize: 12 }}
                >
                  {opts.map((o) => (
                    <MenuItem key={o} value={o} sx={{ fontSize: 12, py: 0.5 }}>
                      <Checkbox
                        size="small"
                        checked={selected.includes(o)}
                        sx={{ p: 0, mr: 1, "& .MuiSvgIcon-root": { fontSize: 14 } }}
                      />
                      {o}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
            );
          } else if (col.type === "date") {
            const fKey = resolveFilterKey(col.key);
            const range = (filters[fKey] as { from: string; to: string }) || { from: "", to: "" };
            return (
              <Box key={col.key}>
                {labelEl}
                <Box sx={{ display: "flex", gap: 0.75 }}>
                  <Box sx={{ flex: 1 }}>
                    <Box
                      component="input"
                      type="date"
                      value={range.from}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        onChange(fKey, { ...range, from: e.target.value })
                      }
                      sx={inputSx}
                    />
                    <Typography sx={{ fontSize: 10, color: "#9ca3af", mt: 0.25 }}>From</Typography>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Box
                      component="input"
                      type="date"
                      value={range.to}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        onChange(fKey, { ...range, to: e.target.value })
                      }
                      sx={inputSx}
                    />
                    <Typography sx={{ fontSize: 10, color: "#9ca3af", mt: 0.25 }}>To</Typography>
                  </Box>
                </Box>
              </Box>
            );
          } else if (col.type === "currency") {
            const fKey = resolveFilterKey(col.key);
            const range = (filters[fKey] as { min: string; max: string }) || { min: "", max: "" };
            return (
              <Box key={col.key}>
                {labelEl}
                <Box sx={{ display: "flex", gap: 0.75 }}>
                  <Box sx={{ flex: 1 }}>
                    <Box
                      component="input"
                      type="number"
                      placeholder="Min"
                      value={range.min}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        onChange(fKey, { ...range, min: e.target.value })
                      }
                      sx={inputSx}
                    />
                    <Typography sx={{ fontSize: 10, color: "#9ca3af", mt: 0.25 }}>Min (₹)</Typography>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Box
                      component="input"
                      type="number"
                      placeholder="Max"
                      value={range.max}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        onChange(fKey, { ...range, max: e.target.value })
                      }
                      sx={inputSx}
                    />
                    <Typography sx={{ fontSize: 10, color: "#9ca3af", mt: 0.25 }}>Max (₹)</Typography>
                  </Box>
                </Box>
              </Box>
            );
          } else {
            const fKey = resolveFilterKey(col.key);
            const val = (filters[fKey] as string) || "";
            return (
              <Box key={col.key}>
                {labelEl}
                <Box
                  component="input"
                  placeholder={`Search…`}
                  value={val}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(fKey, e.target.value)}
                  sx={inputSx}
                />
              </Box>
            );
          }
        })}
      </Box>

      {/* Footer */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 1,
          px: 2.5,
          py: 1.5,
          borderTop: "1px solid #e5e7eb",
          flexShrink: 0,
        }}
      >
        <Button
          size="small"
          onClick={onClear}
          sx={{
            fontSize: 12,
            color: "#6b7280",
            border: "1px solid #e5e7eb",
            borderRadius: "7px",
            px: 2,
            textTransform: "none",
            "&:hover": { backgroundColor: "#f9fafb" },
          }}
        >
          Clear All
        </Button>
        <Button
          size="small"
          onClick={onClose}
          sx={{
            fontSize: 12,
            color: "#374151",
            border: "1px solid #e5e7eb",
            borderRadius: "7px",
            px: 2,
            textTransform: "none",
            "&:hover": { backgroundColor: "#f9fafb" },
          }}
        >
          Cancel
        </Button>
        <Button
          size="small"
          onClick={onClose}
          sx={{
            fontSize: 12,
            color: "#fff",
            backgroundColor: "#5b2b8e",
            borderRadius: "7px",
            px: 2.5,
            textTransform: "none",
            "&:hover": { backgroundColor: "#4c2278" },
          }}
        >
          Apply
        </Button>
      </Box>
    </Dialog>
  );
};

// ─── Main component ────────────────────────────────────────────────────────────

const VendorHold: React.FC = () => {
  const location = useLocation<{ pan?: string } | undefined>();
  const { actor } = useContext(userContext);
  const role =
    actor?.userRole === UserRole.DRMaker
      ? InstamartInternalProcessRole.MAKER
      : actor?.userRole === UserRole.DRChecker
      ? InstamartInternalProcessRole.CHECKER
      : InstamartInternalProcessRole.APPROVER;

  const [activeTab, setActiveTab] = useState(0);
  const [searchText, setSearchText] = useState("");
  const [summary, setSummary] = useState<HoldSummary | null>(null);
  const [tabCounts, setTabCounts] = useState<HoldTabCount | null>(null);
  const [tabRows, setTabRows] = useState<Record<string, any>[]>([]);
  const [tableLoader, setTableLoader] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [uploadingEntityHold, setUploadingEntityHold] = useState(false);
  const [uploadingOtherHold, setUploadingOtherHold] = useState(false);
  const [entityHoldLastUpload, setEntityHoldLastUpload] = useState<string | null>(null);
  const [otherHoldLastUpload, setOtherHoldLastUpload] = useState<string | null>(null);
  const [fullPaymentHoldLastUpload, setFullPaymentHoldLastUpload] = useState<string | null>(null);
  const [uploadingFullPaymentHold, setUploadingFullPaymentHold] = useState(false);
  const [gstScootsyLastUpload, setGstScootsyLastUpload] = useState<string | null>(null);
  const [uploadingGstScootsy, setUploadingGstScootsy] = useState(false);
  const [prRtvWastageLastUpload, setPrRtvWastageLastUpload] = useState<string | null>(null);
  const [rtvYetToPickLastUpload, setRtvYetToPickLastUpload] = useState<string | null>(null);
  const [prRtvUnaccLastUpload, setPrRtvUnaccLastUpload] = useState<string | null>(null);
  const [uploadingPrRtvWastage, setUploadingPrRtvWastage] = useState(false);
  const [uploadingRtvYetToPick, setUploadingRtvYetToPick] = useState(false);
  const [uploadingPrRtvUnacc, setUploadingPrRtvUnacc] = useState(false);
  const [eventsHoldLastUpload, setEventsHoldLastUpload] = useState<string | null>(null);
  const [uploadingEventsHold, setUploadingEventsHold] = useState(false);
  const [adsVisibilityLastUpload, setAdsVisibilityLastUpload] = useState<string | null>(null);
  const [uploadingAdsVisibility, setUploadingAdsVisibility] = useState(false);
  const [bdpoLastUpload, setBdpoLastUpload] = useState<string | null>(null);
  const [uploadingBdpo, setUploadingBdpo] = useState(false);
  const [page, setPage] = useState(0);
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());
  const [tab1VendorCount, setTab1VendorCount] = useState<number | null>(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const [activeFilters, setActiveFilters] = useState<Record<string, any>>(() =>
    location.state?.pan ? { PAN: location.state.pan } : {}
  );

  const companyId = useRef<string>(null);
  const branchCode = useRef<string>(null);
  const fetchGen = useRef(0);

  const fetchVendorHoldData = () => {
    useFetch(API_ENDPOINTS.GET_VENDOR_HOLD_LIST.url, "GET", {
      failureMessage: API_ENDPOINTS.GET_VENDOR_HOLD_LIST.failureMessage,
      config: {
        params: {
          companyId: companyId.current,
          branchCode: branchCode.current,
        },
      },
      thenCallBack: (response) => {
        setSummary(response.data?.summary ?? null);
        setTabCounts(response.data?.tabCounts ?? null);
        setEntityHoldLastUpload(response.data?.entityHoldLastUpload ?? null);
        setOtherHoldLastUpload(response.data?.otherHoldLastUpload ?? null);
        setFullPaymentHoldLastUpload(response.data?.fullPaymentHoldLastUpload ?? null);
        setGstScootsyLastUpload(response.data?.gstScootsyLastUpload ?? null);
        setPrRtvWastageLastUpload(response.data?.prRtvWastageLastUpload ?? null);
        setRtvYetToPickLastUpload(response.data?.rtvYetToPickLastUpload ?? null);
        setPrRtvUnaccLastUpload(response.data?.prRtvUnaccLastUpload ?? null);
        setEventsHoldLastUpload(response.data?.eventsHoldLastUpload ?? null);
        setAdsVisibilityLastUpload(response.data?.adsVisibilityLastUpload ?? null);
        setBdpoLastUpload(response.data?.bdpoLastUpload ?? null);
      },
    });
  };

  const fetchTabData = () => {
    const apiTab = UI_TAB_TO_API_TAB[activeTab];
    if (!apiTab) {
      setTabRows([]);
      return;
    }
    const gen = ++fetchGen.current;
    setTableLoader(true);
    setPage(0);
    setSelectedRows(new Set());
    setTab1VendorCount(null);
    useFetch(API_ENDPOINTS.FETCH_VENDOR_HOLD_TAB(role, apiTab).url, "GET", {
      failureMessage: API_ENDPOINTS.FETCH_VENDOR_HOLD_TAB(role, apiTab).failureMessage,
      thenCallBack: (response) => {
        if (gen !== fetchGen.current) return;
        setTabRows(response.data?.data?.rows ?? []);
        if (activeTab === 1) setTab1VendorCount(response.data?.data?.totalHoldAmount ?? null);
        setTableLoader(false);
      },
      catchCallBack: () => {
        if (gen !== fetchGen.current) return;
        setTableLoader(false);
      },
    });
  };

  useEffect(() => {
    fetchVendorHoldData();
    fetchTabData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const uploadHoldFile = (
    file: File,
    reportKey:
      | "entityHold"
      | "otherHold"
      | "fullPaymentHold"
      | "gstScootsy"
      | "prRtvWastage"
      | "rtvYetToPick"
      | "prRtvUnacc"
      | "eventsHold"
      | "adsVisibility"
      | "bdpo"
  ) => {
    const setLoading =
      reportKey === "entityHold"
        ? setUploadingEntityHold
        : reportKey === "otherHold"
        ? setUploadingOtherHold
        : reportKey === "gstScootsy"
        ? setUploadingGstScootsy
        : reportKey === "prRtvWastage"
        ? setUploadingPrRtvWastage
        : reportKey === "rtvYetToPick"
        ? setUploadingRtvYetToPick
        : reportKey === "prRtvUnacc"
        ? setUploadingPrRtvUnacc
        : reportKey === "eventsHold"
        ? setUploadingEventsHold
        : reportKey === "adsVisibility"
        ? setUploadingAdsVisibility
        : reportKey === "bdpo"
        ? setUploadingBdpo
        : setUploadingFullPaymentHold;
    const label =
      reportKey === "entityHold"
        ? "Entity-wise hold"
        : reportKey === "otherHold"
        ? "Other hold amounts"
        : reportKey === "gstScootsy"
        ? "GST Hold"
        : reportKey === "prRtvWastage"
        ? "PR RTV Wastage"
        : reportKey === "rtvYetToPick"
        ? "RTV Yet to Pick"
        : reportKey === "prRtvUnacc"
        ? "PR/RTV — Unaccounted"
        : reportKey === "eventsHold"
        ? "Events Hold"
        : reportKey === "adsVisibility"
        ? "Ads Visibility Hold"
        : reportKey === "bdpo"
        ? "BDPO Hold"
        : "Full payment hold vendors";
    const reportTypeMap: Record<string, string> = {
      entityHold: OnHoldReportType.ENTITY_WISE_HOLD,
      otherHold: OnHoldReportType.OTHER_HOLD,
      fullPaymentHold: OnHoldReportType.FULL_PAYMENT_HOLD,
      gstScootsy: OnHoldReportType.GST_SCOOTSY,
      prRtvWastage: OnHoldReportType.PR_RTV_WASTAGE,
      rtvYetToPick: OnHoldReportType.RTV_YET_TO_PICK,
      prRtvUnacc: OnHoldReportType.PRRTV_UNACCOUNTED,
      eventsHold: OnHoldReportType.EVENTS_HOLD,
      adsVisibility: OnHoldReportType.ADS_VISIBILITY,
      bdpo: OnHoldReportType.BDPO,
    };
    setLoading(true);
    const formData = new FormData();
    formData.append("file", file);
    const uploadEndpoint = API_ENDPOINTS.UPLOAD_VENDOR_HOLD_FILE(role, reportTypeMap[reportKey]);
    useFetch(uploadEndpoint.url, "POST", {
      failureMessage: uploadEndpoint.failureMessage,
      data: formData,
      thenCallBack: () => {
        toast.success(<CustomToast message={`${label} file uploaded successfully`} />);
        fetchVendorHoldData();
        fetchTabData();
        setLoading(false);
      },
      catchCallBack: () => setLoading(false),
    });
  };

  const handleDownloadReport = () => {
    setIsDownloading(true);
    useFetch(API_ENDPOINTS.DOWNLOAD_VENDOR_HOLD_REPORT.url, "GET", {
      failureMessage: API_ENDPOINTS.DOWNLOAD_VENDOR_HOLD_REPORT.failureMessage,
      config: {
        params: {
          tab: TAB_FILTER_MAP[activeTab],
          companyId: companyId.current,
          branchCode: branchCode.current,
        },
      },
      thenCallBack: (response) => {
        const binaryStr = atob(response.data.data as string);
        const bytes = new Uint8Array(binaryStr.length);
        for (let i = 0; i < binaryStr.length; i++) bytes[i] = binaryStr.charCodeAt(i);
        saveAs(
          new Blob([bytes], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
          `VendorHold_Report_${new Date().toISOString().slice(0, 10)}.xlsx`
        );
        setIsDownloading(false);
      },
      catchCallBack: () => setIsDownloading(false),
    });
  };

  const activeColDefs = TAB_COLUMN_DEFS[activeTab] ?? [];

  const filteredRows = tabRows.filter((row) => {
    if (searchText) {
      const q = searchText.toLowerCase();
      if (!Object.values(row).some((v) => v !== null && String(v).toLowerCase().includes(q))) return false;
    }

    for (const colDef of activeColDefs) {
      const filterKey = resolveFilterKey(colDef.key);
      const colKey = colDef.key;
      const cellVal = row[colKey];

      if (filterKey in activeFilters) {
        const filterVal = activeFilters[filterKey];

        if (colDef.type === "boolean") {
          const selected = filterVal as string[];
          if (selected && selected.length > 0) {
            const resolved = cellVal === true || cellVal === "true";
            if (!selected.includes(resolved ? "Resolved" : "Pending")) return false;
          }
        } else if (colDef.type === "text" && DROPDOWN_TEXT_COLS.has(colKey)) {
          const selected = filterVal as string[];
          if (selected && selected.length > 0 && !selected.includes(String(cellVal ?? ""))) return false;
        } else if (colDef.type === "text") {
          const q = ((filterVal as string) || "").toLowerCase();
          if (
            q &&
            !String(cellVal ?? "")
              .toLowerCase()
              .includes(q)
          )
            return false;
        } else if (colDef.type === "date") {
          const range = filterVal as { from: string; to: string };
          if (range && (range.from || range.to)) {
            if (!cellVal) return false;
            const d = new Date(cellVal as string);
            if (range.from && d < new Date(range.from)) return false;
            if (range.to && d > new Date(range.to + "T23:59:59")) return false;
          }
        } else if (colDef.type === "currency") {
          const range = filterVal as { min: string; max: string };
          if (range) {
            const amount = Number(cellVal);
            if (range.min !== "" && range.min !== undefined && amount < Number(range.min)) return false;
            if (range.max !== "" && range.max !== undefined && amount > Number(range.max)) return false;
          }
        }
      }
    }

    return true;
  });

  const holdAmountKey = TAB_HOLD_AMOUNT_KEY[activeTab];
  const computedTotalHoldAmount = holdAmountKey
    ? filteredRows.reduce((sum, row) => sum + (Number(row[holdAmountKey]) || 0), 0)
    : null;

  const paginatedRows = filteredRows.slice(page * ROWS_PER_PAGE, (page + 1) * ROWS_PER_PAGE);
  const allSelected =
    paginatedRows.length > 0 && paginatedRows.every((_, i) => selectedRows.has(page * ROWS_PER_PAGE + i));
  const toggleAll = () => {
    const next = new Set(selectedRows);
    if (allSelected) {
      paginatedRows.forEach((_, i) => next.delete(page * ROWS_PER_PAGE + i));
    } else {
      paginatedRows.forEach((_, i) => next.add(page * ROWS_PER_PAGE + i));
    }
    setSelectedRows(next);
  };
  const toggleRow = (absIdx: number) => {
    const next = new Set(selectedRows);
    next.has(absIdx) ? next.delete(absIdx) : next.add(absIdx);
    setSelectedRows(next);
  };

  const activeFilterCount = activeColDefs.filter((colDef) => {
    const v = activeFilters[resolveFilterKey(colDef.key)];
    if (Array.isArray(v)) return v.length > 0;
    if (v && typeof v === "object") return Object.values(v).some((x) => x !== "" && x !== null && x !== undefined);
    return v !== "" && v !== null && v !== undefined;
  }).length;

  const tabCountValues: (number | undefined)[] = [
    tabCounts?.panLevelHold,
    tabCounts?.fullPaymentHold,
    tabCounts?.gstRelatedHold,
    tabCounts?.prRtvRelatedHold,
    tabCounts?.eventsHold,
    tabCounts?.adsVisibilityHold,
    tabCounts?.bdpoHold,
  ];

  return (
    <LoggedInSkeleton topBarButtons={getReconTopBarButtons("Dispute Resolution", actor?.name, {}, actor)}>
      <Box sx={{ backgroundColor: "#f9fafb", minHeight: "100vh" }}>
        {/* Page header */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", px: 3, pt: 2.25, pb: 1 }}>
          <Typography sx={{ fontSize: 19, fontWeight: 700, color: "#111827" }}>Vendor Hold</Typography>
          <CustomButton
            variant="outlined"
            size="small"
            colorToken="violet"
            startIcon={isDownloading ? <CircularProgress size={14} /> : <Download sx={{ fontSize: 14 }} />}
            disabled={isDownloading}
            onClick={handleDownloadReport}
          >
            Download Report
          </CustomButton>
        </Box>

        {/* Summary cards */}
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 1.25, px: 3, mb: 1.5 }}>
          <SummaryCard
            icon={<PeopleAlt />}
            iconBg="#ede9fe"
            iconColor="#7c3aed"
            value={summary?.vendorsOnHold?.toLocaleString("en-IN") ?? "—"}
            label="Vendors on Hold"
            subLabel="across all hold types"
          />
          <SummaryCard
            icon={<Block />}
            iconBg="#fee2e2"
            iconColor="#dc2626"
            value={summary?.fullPaymentBlocked?.toLocaleString("en-IN") ?? "—"}
            label="Full Payment Blocked"
            subLabel="PAN-level blocks"
          />
          <SummaryCard
            icon={<WarningAmber />}
            iconBg="#fff7ed"
            iconColor="#ea580c"
            value={
              summary !== null &&
              summary !== undefined &&
              summary.totalHoldAmount !== null &&
              summary.totalHoldAmount !== undefined
                ? `₹${(summary.totalHoldAmount / 1e7).toFixed(1)} Cr`
                : "—"
            }
            label="Total Hold Amount"
            subLabel="across all buckets"
          />
          <SummaryCard
            icon={<AccessTimeIcon />}
            iconBg="#fffbeb"
            iconColor="#d97706"
            value={summary?.lastUploadDate ? "Today" : "—"}
            label="Last Upload"
            subLabel={
              summary?.lastUploadDate
                ? convertUtcToIst(new Date(summary.lastUploadDate), "DD MMM YYYY, hh:mm A")
                : undefined
            }
          />
        </Box>

        {/* Tabs */}
        <Box sx={{ borderBottom: "1px solid #e5e7eb", backgroundColor: "#fff" }}>
          <Tabs
            value={activeTab}
            onChange={(_e, val) => {
              setActiveTab(val);
              setPage(0);
              setTabRows([]);
              setSearchText("");
              setActiveFilters((prev) => {
                const carried: Record<string, any> = {};
                for (const key of CROSS_TAB_FILTER_KEYS) {
                  if (prev[key] !== undefined) carried[key] = prev[key];
                }
                return carried;
              });
              setTableLoader(true);
            }}
            variant="scrollable"
            scrollButtons="auto"
            TabIndicatorProps={{ style: { backgroundColor: "#5b2b8e" } }}
            sx={{
              minHeight: 44,
              px: 3,
              "& .MuiTab-root": {
                textTransform: "none",
                fontSize: 12,
                fontWeight: 600,
                minHeight: 44,
                color: "#6b7280",
                px: 1.75,
              },
              "& .Mui-selected": { color: "#5b2b8e" },
            }}
          >
            {TAB_NAMES.map((name, i) => (
              <Tab key={name} label={<TabLabel label={name} count={tabCountValues[i]} danger={i === 0 || i === 1} />} />
            ))}
          </Tabs>
        </Box>

        {/* Tab content */}
        <Box sx={{ p: 3 }}>
          {/* Total Amount / Vendor Count bar */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
            <Typography sx={{ fontSize: 12, color: "#6b7280", fontWeight: 500 }}>
              {activeTab === 1 ? "Vendors Count" : "Total Hold Amount"}
            </Typography>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#dc2626" }}>
              {activeTab === 1
                ? tab1VendorCount !== null
                  ? tab1VendorCount.toLocaleString("en-IN")
                  : "—"
                : computedTotalHoldAmount !== null
                ? formatMoney(ToDineroObj(computedTotalHoldAmount))
                : "—"}
            </Typography>
          </Box>

          {/* Upload cards — tab-specific */}
          {activeTab === 1 ? (
            <Box sx={{ display: "flex", gap: 1.5, mb: 1.75 }}>
              <Box sx={{ width: "50%" }}>
                <UploadCard
                  title="Hold Vendors"
                  fileTitle="Upload Hold Vendors"
                  fileSubtitle="Payment Working File · Sheet 4"
                  lastUpload={fullPaymentHoldLastUpload}
                  onUpload={(f) => uploadHoldFile(f, "fullPaymentHold")}
                  uploading={uploadingFullPaymentHold}
                  reportType={OnHoldReportType.FULL_PAYMENT_HOLD}
                  role={role}
                />
              </Box>
            </Box>
          ) : activeTab === 2 ? (
            <Box sx={{ display: "flex", gap: 1.5, mb: 1.75 }}>
              <Box sx={{ width: "50%" }}>
                <UploadCard
                  title="GST Hold"
                  fileTitle="Upload GST Hold"
                  fileSubtitle="Hold Details File · Sheet: GST - Hold"
                  lastUpload={gstScootsyLastUpload}
                  onUpload={(f) => uploadHoldFile(f, "gstScootsy")}
                  uploading={uploadingGstScootsy}
                  reportType={OnHoldReportType.GST_SCOOTSY}
                  role={role}
                />
              </Box>
            </Box>
          ) : activeTab === 3 ? (
            <Box sx={{ display: "flex", gap: 1.5, mb: 1.75 }}>
              <UploadCard
                title="PR RTV Wastage"
                fileTitle="Upload PR RTV Wastage"
                fileSubtitle="Hold Details File · Sheet: PR RTV Wastage"
                lastUpload={prRtvWastageLastUpload}
                onUpload={(f) => uploadHoldFile(f, "prRtvWastage")}
                uploading={uploadingPrRtvWastage}
                reportType={OnHoldReportType.PR_RTV_WASTAGE}
                role={role}
              />
              <UploadCard
                title="RTV Yet to Pick"
                fileTitle="Upload RTV Yet to Pick"
                fileSubtitle="Hold Details File · Sheet: RTV yet to pick"
                lastUpload={rtvYetToPickLastUpload}
                onUpload={(f) => uploadHoldFile(f, "rtvYetToPick")}
                uploading={uploadingRtvYetToPick}
                reportType={OnHoldReportType.RTV_YET_TO_PICK}
                role={role}
              />
              <UploadCard
                title="PR/RTV — Unaccounted"
                fileTitle="Upload PR/RTV — Unaccounted"
                fileSubtitle="Hold Details File · Sheet: PR/RTV-Unacc"
                lastUpload={prRtvUnaccLastUpload}
                onUpload={(f) => uploadHoldFile(f, "prRtvUnacc")}
                uploading={uploadingPrRtvUnacc}
                reportType={OnHoldReportType.PRRTV_UNACCOUNTED}
                role={role}
              />
            </Box>
          ) : activeTab === 4 ? (
            <Box sx={{ display: "flex", gap: 1.5, mb: 1.75 }}>
              <Box sx={{ width: "50%" }}>
                <UploadCard
                  title="Events Hold File"
                  fileTitle="Upload Events Hold File"
                  fileSubtitle="Hold Details File · Events data"
                  lastUpload={eventsHoldLastUpload}
                  onUpload={(f) => uploadHoldFile(f, "eventsHold")}
                  uploading={uploadingEventsHold}
                  reportType={OnHoldReportType.EVENTS_HOLD}
                  role={role}
                />
              </Box>
            </Box>
          ) : activeTab === 5 ? (
            <Box sx={{ display: "flex", gap: 1.5, mb: 1.75 }}>
              <Box sx={{ width: "50%" }}>
                <UploadCard
                  title="Ads Visibility Hold File"
                  fileTitle="Upload Ads Visibility Hold File"
                  fileSubtitle="Hold Details File · Sheet: ads visibility"
                  lastUpload={adsVisibilityLastUpload}
                  onUpload={(f) => uploadHoldFile(f, "adsVisibility")}
                  uploading={uploadingAdsVisibility}
                  reportType={OnHoldReportType.ADS_VISIBILITY}
                  role={role}
                />
              </Box>
            </Box>
          ) : activeTab === 6 ? (
            <Box sx={{ display: "flex", gap: 1.5, mb: 1.75 }}>
              <Box sx={{ width: "50%" }}>
                <UploadCard
                  title="BDPO Hold File"
                  fileTitle="Upload BDPO Hold File"
                  fileSubtitle="Hold Details File · Sheet: BDPO"
                  lastUpload={bdpoLastUpload}
                  onUpload={(f) => uploadHoldFile(f, "bdpo")}
                  uploading={uploadingBdpo}
                  reportType={OnHoldReportType.BDPO}
                  role={role}
                />
              </Box>
            </Box>
          ) : (
            <Box sx={{ display: "flex", gap: 1.5, mb: 1.75 }}>
              <UploadCard
                title="Entity-wise Hold Amounts"
                fileTitle="Upload Entity-wise Hold file"
                fileSubtitle="Excel (.xlsx) · Payment Working File, Sheet 2"
                lastUpload={entityHoldLastUpload}
                onUpload={(f) => uploadHoldFile(f, "entityHold")}
                uploading={uploadingEntityHold}
                reportType={OnHoldReportType.ENTITY_WISE_HOLD}
                role={role}
              />
              <UploadCard
                title="Other Hold Amounts"
                fileTitle="Upload Other Hold Amounts file"
                fileSubtitle="Excel (.xlsx) · Payment Working File, Sheet 3"
                lastUpload={otherHoldLastUpload}
                onUpload={(f) => uploadHoldFile(f, "otherHold")}
                uploading={uploadingOtherHold}
                reportType={OnHoldReportType.OTHER_HOLD}
                role={role}
              />
            </Box>
          )}

          {/* Table card */}
          <Box sx={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: "10px", overflow: "hidden" }}>
            {/* Toolbar */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: "15px",
                py: "11px",
                borderBottom: "1px solid #f3f4f6",
              }}
            >
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#111827" }}>
                {TAB_NAMES[activeTab]}
                {filteredRows.length > 0 && (
                  <Box component="span" sx={{ ml: 0.5 }}>
                    {" "}
                    ({filteredRows.length.toLocaleString("en-IN")})
                  </Box>
                )}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.875 }}>
                <Box
                  component="input"
                  placeholder="Search vendor, PAN…"
                  value={searchText}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setSearchText(e.target.value);
                    setPage(0);
                  }}
                  sx={{
                    border: "1px solid #e5e7eb",
                    borderRadius: "7px",
                    padding: "6px 10px 6px 30px",
                    fontSize: 12,
                    color: "#374151",
                    background:
                      "#fff url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12' fill='none'%3E%3Ccircle cx='5' cy='5' r='4' stroke='%239CA3AF' stroke-width='1.3'/%3E%3Cpath d='M8 8l2.5 2.5' stroke='%239CA3AF' stroke-width='1.3' stroke-linecap='round'/%3E%3C/svg%3E\") no-repeat 10px center",
                    width: 200,
                    outline: "none",
                    fontFamily: "inherit",
                    "&:focus": { borderColor: "#5b2b8e" },
                  }}
                />
                <Button
                  size="small"
                  startIcon={<FilterAltOutlined sx={{ fontSize: 12 }} />}
                  onClick={() => setFilterOpen(true)}
                  sx={{
                    fontSize: 12,
                    color: activeFilterCount > 0 ? "#5b2b8e" : "#374151",
                    border: `1px solid ${activeFilterCount > 0 ? "#5b2b8e" : "#e5e7eb"}`,
                    borderRadius: "7px",
                    px: 1.375,
                    py: 0.625,
                    textTransform: "none",
                    backgroundColor: activeFilterCount > 0 ? "#f5f0fc" : "#fff",
                    "&:hover": { backgroundColor: activeFilterCount > 0 ? "#ede9fe" : "#f9fafb" },
                  }}
                >
                  Filter{activeFilterCount > 0 ? ` (${activeFilterCount})` : ""}
                </Button>
                <Button
                  size="small"
                  startIcon={<IosShare sx={{ fontSize: 12 }} />}
                  onClick={handleDownloadReport}
                  sx={{
                    fontSize: 12,
                    color: "#374151",
                    border: "1px solid #e5e7eb",
                    borderRadius: "7px",
                    px: 1.375,
                    py: 0.625,
                    textTransform: "none",
                    backgroundColor: "#fff",
                    "&:hover": { backgroundColor: "#f9fafb" },
                  }}
                >
                  Export
                </Button>
              </Box>
            </Box>

            {/* Table */}
            <Box sx={{ overflowX: "auto" }}>
              {tableLoader ? (
                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 6 }}>
                  <CircularProgress size={28} sx={{ color: "#5b2b8e" }} />
                </Box>
              ) : (
                <Table sx={{ width: "100%", borderCollapse: "collapse" }}>
                  <TableHead>
                    <TableRow>
                      <TableCell
                        padding="checkbox"
                        sx={{ ...thSx, cursor: "default", "&:hover": {}, width: 40, pl: "12px" }}
                      >
                        <Checkbox
                          size="small"
                          checked={allSelected}
                          indeterminate={selectedRows.size > 0 && !allSelected}
                          onChange={toggleAll}
                          sx={{ p: 0, "& .MuiSvgIcon-root": { fontSize: 15 } }}
                        />
                      </TableCell>
                      {activeColDefs.map((col) => (
                        <TableCell key={col.key} sx={thSx}>
                          {col.label} &#x2195;
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedRows.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={activeColDefs.length + 1}
                          sx={{ textAlign: "center", py: 5, fontSize: 12, color: "#9ca3af", borderBottom: "none" }}
                        >
                          {UI_TAB_TO_API_TAB[activeTab] ? "No data found" : "No data available for this tab yet"}
                        </TableCell>
                      </TableRow>
                    ) : (
                      paginatedRows.map((row, i) => {
                        const absIdx = page * ROWS_PER_PAGE + i;
                        const isSelected = selectedRows.has(absIdx);
                        const isLast = i === paginatedRows.length - 1;
                        return (
                          <TableRow
                            key={absIdx}
                            selected={isSelected}
                            sx={{
                              "&:hover td": { backgroundColor: "#fafafa" },
                              "& td": { borderBottom: isLast ? "none" : "1px solid #f3f4f6" },
                            }}
                          >
                            <TableCell padding="checkbox" sx={{ ...tdSx, borderBottom: "inherit", pl: "12px" }}>
                              <Checkbox
                                size="small"
                                checked={isSelected}
                                onChange={() => toggleRow(absIdx)}
                                sx={{ p: 0, "& .MuiSvgIcon-root": { fontSize: 15 } }}
                              />
                            </TableCell>
                            {activeColDefs.map((col) => {
                              const val = row[col.key];
                              let content: React.ReactNode;
                              if (val === null || val === undefined) {
                                content = "—";
                              } else if (col.type === "boolean") {
                                const resolved = val === true || val === "true";
                                content = (
                                  <Box
                                    component="span"
                                    sx={{
                                      fontSize: 11,
                                      fontWeight: 600,
                                      px: 1,
                                      py: 0.25,
                                      borderRadius: "100px",
                                      backgroundColor: resolved ? "#d1fae5" : "#fee2e2",
                                      color: resolved ? "#065f46" : "#991b1b",
                                    }}
                                  >
                                    {resolved ? "Resolved" : "Pending"}
                                  </Box>
                                );
                              } else if (col.type === "currency") {
                                const amount = Number(val);
                                content = (
                                  <Box component="span" sx={{ fontWeight: 600, color: "#dc2626" }}>
                                    {formatMoney(ToDineroObj(Number.isFinite(amount) ? amount : 0))}
                                  </Box>
                                );
                              } else if (col.type === "date") {
                                content = convertUtcToIst(new Date(val as string), "DD MMM YYYY");
                              } else {
                                content = String(val);
                              }
                              return (
                                <TableCell key={col.key} sx={{ ...tdSx, borderBottom: "inherit" }}>
                                  {content}
                                </TableCell>
                              );
                            })}
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              )}
            </Box>

            {/* Pagination */}
            {!tableLoader && filteredRows.length > 0 && (
              <PaginationBar
                total={filteredRows.length}
                page={page}
                rowsPerPage={ROWS_PER_PAGE}
                onPageChange={setPage}
                label="records"
              />
            )}
          </Box>
        </Box>
      </Box>

      <FilterDrawer
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        colDefs={activeColDefs}
        rows={tabRows}
        filters={activeFilters}
        onChange={(key, value) => {
          setActiveFilters((prev) => ({ ...prev, [key]: value }));
          setPage(0);
        }}
        onClear={() => {
          setActiveFilters({});
          setPage(0);
        }}
      />
    </LoggedInSkeleton>
  );
};

export default VendorHold;
